"use client";

/**
 * A shop's collection page: its cover and line about it, its products — in
 * the shop's own order, or by price or newest — 24 at a time, and the
 * services in it (a bouquet made to order, a room set-up — each with its Book
 * button, the same card as the Services tab): before the products when the
 * shop shows its services first (Brand Settings → Storefront), else after
 * them. One-off reads (not a live subscription): a visitor browsing a
 * collection doesn't need it re-sent every time the shop edits a product.
 * The visitor's branch (?store=, or the one they picked before) narrows a
 * collection the whole business shares.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useQuery } from "convex/react";
import { fetchQuery } from "convex/nextjs";
import { api } from "@/convex/_generated/api";
import ShopHeader from "../../components/ShopHeader";
import { ServiceCard, type PublicService } from "../../components/ServiceCard";
import { ProductGrid } from "@/components/shop/ProductGrid";
import { toShopProduct } from "@/lib/shop/toShopProduct";
import { loadMenuStock } from "@/lib/shop/menuStock";
import type { ProductPagination, ShopProduct } from "@/lib/types/shop";
import { ChevronLeft, Layers, Sparkles } from "lucide-react";

type Sort = "featured" | "price_low" | "price_high" | "newest";

const SORTS: { id: Sort; label: string }[] = [
  { id: "featured", label: "Featured" },
  { id: "price_low", label: "Price: low to high" },
  { id: "price_high", label: "Price: high to low" },
  { id: "newest", label: "Newest" },
];

const FIRST_PAGE: ProductPagination = { page: 1, limit: 24, total: 0, pages: 1, hasNext: false, hasPrev: false };

interface CollectionHeader {
  name: string;
  slug: string;
  description: string | null;
  cover: string | null;
}

export default function CollectionPageClient({
  domain,
  slug,
  industry,
}: {
  domain: string;
  slug: string;
  industry: "fashion" | "electronics";
}) {
  const brand = useQuery(api.brands.core.getBrandBySubdomain, { subdomain: (domain || "").toLowerCase() }) as any;
  const brandId = brand?._id as string | undefined;

  const [sort, setSort] = useState<Sort>("featured");
  const [header, setHeader] = useState<CollectionHeader | null | undefined>(undefined); // undefined = loading, null = none
  const [products, setProducts] = useState<ShopProduct[]>([]);
  // The collection's services come with its first page only.
  const [services, setServices] = useState<PublicService[]>([]);
  const [pagination, setPagination] = useState<ProductPagination>(FIRST_PAGE);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const request = useRef(0);
  // undefined until read; "" = no branch picked. Read in the browser — the
  // address, else the branch they picked on the shop (BrandShopWrapper).
  const [visitorStore, setVisitorStore] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!brandId) return;
    let picked = "";
    try {
      picked = new URLSearchParams(window.location.search).get("store") || localStorage.getItem(`shop_store_${brandId}`) || "";
    } catch {
      picked = "";
    }
    setVisitorStore(picked);
  }, [brandId]);

  const load = useCallback(
    async (page: number) => {
      if (!brandId || visitorStore === undefined) return;
      const id = ++request.current;
      const data: any = await fetchQuery(api.collections.shop.collectionPage, {
        brandId: brandId as any,
        slug,
        sort,
        page,
        ...(visitorStore ? { storeId: visitorStore } : {}),
      });
      if (id !== request.current) return; // a newer sort or page asked since
      if (!data) {
        setHeader(null);
        setProducts([]);
        setServices([]);
        return;
      }
      setHeader(data.collection);
      // A drink in a collection sells out like anywhere else — its real stock,
      // not the "sold out" a missing figure used to read as.
      const menuStock = await loadMenuStock(String(brandId), data.products);
      if (id !== request.current) return;
      const rows: ShopProduct[] = data.products.map((p: any) => toShopProduct(p, { brandId: String(brandId), industry, menuStock }));
      setProducts((prev) => (page > 1 ? [...prev, ...rows] : rows));
      // Page 1 (a first load, or a new sort) brings the services; "See more"
      // pages carry none, and must not take them off the page.
      if (page === 1) setServices((data.services ?? []) as PublicService[]);
      setPagination(data.pagination);
    },
    [brandId, slug, sort, industry, visitorStore],
  );

  useEffect(() => {
    if (!brandId || visitorStore === undefined) return;
    setLoading(true);
    setFailed(false);
    load(1)
      .catch((e) => {
        console.error("[CollectionPage] load failed:", e);
        setFailed(true);
      })
      .finally(() => setLoading(false));
  }, [brandId, visitorStore, load]);

  const loadMore = async () => {
    if (loadingMore || !pagination.hasNext) return;
    setLoadingMore(true);
    try {
      await load(pagination.page + 1);
    } catch (e) {
      console.error("[CollectionPage] load more failed:", e);
    } finally {
      setLoadingMore(false);
    }
  };

  const gone = brand === null || header === null;
  // Book falls back to the shop's WhatsApp, as on the Services tab (BrandShopWrapper).
  const whatsappNumber: string | undefined =
    brand?.socialLinks?.whatsapp || brand?.whatsappNumber || brand?.phone || undefined;
  // A collection of services only: no sort, empty grid or "nothing here" under them.
  const servicesOnly = services.length > 0 && !loading && !failed && products.length === 0;
  // The owner's "Show services before products on my website".
  const servicesFirst = brand?.servicesFirstOnWebsite === true;

  // The collection's services — the Services tab's heading and cards.
  const servicesSection =
    services.length > 0 ? (
      <section className={servicesOnly ? "" : servicesFirst ? "mb-10" : "mt-12"}>
        <div className="flex items-center gap-2.5 mb-6">
          <Sparkles className="w-5 h-5" style={{ color: "#0f172a" }} />
          <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wide" style={{ color: "#0f172a" }}>
            Services
          </h2>
          <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: "#f1f5f9", color: "#64748b" }}>
            {services.length}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {services.map((svc) => (
            <ServiceCard key={svc._id} service={svc} brandName={brand?.name ?? ""} whatsappNumber={whatsappNumber} />
          ))}
        </div>
      </section>
    ) : null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FBFCFD" }}>
      <ShopHeader brandName={brand?.name ?? ""} />

      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-6 pt-5">
          <Link
            href={visitorStore ? `/?store=${encodeURIComponent(visitorStore)}` : "/"}
            className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest hover:underline underline-offset-4"
            style={{ color: "#64748b" }}
          >
            <ChevronLeft className="w-4 h-4" /> {brand?.name ? `All of ${brand.name}` : "Back to the shop"}
          </Link>
        </div>

        {/* The collection's cover and line about it. The banner is wide and a
            cover is usually upright (the tiles are 4:5), so the cover is never
            stretched across it: that kept only a thin strip of the middle,
            a bride's dress without her face (Petal_Some Ug., 28 Oct 2026).
            The whole picture sits at the right on a soft blur of itself, as
            the story cards show a picture of the wrong shape. */}
        <div className="max-w-7xl mx-auto px-6 pt-4">
          {header === undefined && !gone ? (
            <div className="rounded-2xl h-44 sm:h-64 animate-pulse" style={{ backgroundColor: "#e2e8f0" }} />
          ) : header ? (
            <div className="relative rounded-2xl overflow-hidden h-44 sm:h-64" style={{ backgroundColor: "#0f172a" }}>
              {header.cover ? (
                <>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={header.cover} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-70" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(15,23,42,0.85) 0%, rgba(15,23,42,0.55) 45%, rgba(15,23,42,0.15) 100%)" }} />
                  <div className="absolute inset-y-3 right-3 left-[40%] sm:inset-y-5 sm:right-5 flex items-center justify-end">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={header.cover}
                      alt={header.name}
                      className="block min-w-0 max-h-full max-w-full w-auto h-auto rounded-xl object-contain shadow-2xl ring-1 ring-white/10"
                    />
                  </div>
                  {/* A wide picture on a phone reaches under the words: shade
                      only that corner (an upright one never gets this far). */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: "radial-gradient(ellipse 75% 100% at 0% 100%, rgba(15,23,42,0.7) 0%, rgba(15,23,42,0.35) 45%, rgba(15,23,42,0) 70%)" }}
                  />
                </>
              ) : (
                <>
                  <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #0f172a 0%, #334155 100%)" }} />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(15,23,42,0.85) 0%, rgba(15,23,42,0.25) 55%, rgba(15,23,42,0) 100%)" }} />
                </>
              )}
              <div className="absolute inset-x-0 bottom-0 p-5 sm:p-8">
                {/* With a cover the words keep to the left, clear of the picture. */}
                <div className={header.cover ? "max-w-[60%]" : undefined}>
                  <h1
                    className={`${header.cover ? "text-xl" : "text-2xl"} sm:text-4xl font-black uppercase tracking-tight text-white [overflow-wrap:anywhere]`}
                    style={{ textWrap: "balance" as any, textShadow: "0 1px 2px rgba(15,23,42,0.6), 0 2px 12px rgba(15,23,42,0.45)" }}
                  >
                    {header.name}
                  </h1>
                  {header.description ? (
                    <p
                      className="mt-1.5 max-w-2xl text-sm sm:text-base text-white/85 line-clamp-2"
                      style={{ textShadow: "0 1px 2px rgba(15,23,42,0.6)" }}
                    >
                      {header.description}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="max-w-7xl mx-auto px-6 py-6 lg:py-8">
          {gone ? (
            <div className="py-20 text-center">
              <Layers className="mx-auto w-8 h-8" style={{ color: "#94a3b8" }} />
              <p className="mt-3 text-base font-bold" style={{ color: "#0f172a" }}>This collection isn&apos;t available</p>
              <p className="mt-1 text-sm" style={{ color: "#64748b" }}>The shop may have taken it down for now.</p>
              <Link href="/" className="mt-5 inline-block text-sm font-bold uppercase tracking-widest border-b border-black pb-1" style={{ color: "#0f172a" }}>
                See all products
              </Link>
            </div>
          ) : (
            <>
              {servicesFirst && servicesSection}

              {!servicesOnly && (
                <>
                  {/* The grid says how many; the sort sits above it. */}
                  <div className="flex items-center justify-end mb-2">
                    <label className="flex items-center gap-2 text-sm" style={{ color: "#0f172a" }}>
                      <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#64748b" }}>Sort</span>
                      <select
                        value={sort}
                        onChange={(e) => setSort(e.target.value as Sort)}
                        className="h-9 rounded-lg border px-3 text-sm bg-white"
                        style={{ borderColor: "#e2e8f0", color: "#0f172a" }}
                      >
                        {SORTS.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  {failed ? (
                    <div className="py-16 text-center text-sm" style={{ color: "#64748b" }}>
                      Couldn&apos;t load this collection. Check your connection and refresh the page.
                    </div>
                  ) : !loading && products.length === 0 ? (
                    <div className="py-16 text-center">
                      <p className="text-sm" style={{ color: "#64748b" }}>Nothing in this collection right now.</p>
                      <Link href="/" className="mt-4 inline-block text-sm font-bold uppercase tracking-widest border-b border-black pb-1" style={{ color: "#0f172a" }}>
                        See all products
                      </Link>
                    </div>
                  ) : (
                    <ProductGrid
                      products={products}
                      pagination={pagination}
                      onLoadMore={loadMore}
                      isLoadingMore={loading || loadingMore}
                      hasAnyProducts={products.length > 0 || loading}
                      gridCols={4}
                      industry={industry}
                    />
                  )}
                </>
              )}

              {!servicesFirst && servicesSection}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
